import { makeEnvelope, wsEnvelopeSchema, type WsEnvelope } from "@cos/shared";
import { reconnectDelayMs } from "./backoff.js";

export type WidgetSocketState = "connecting" | "open" | "reconnecting" | "closed";

export interface WidgetSocketOptions {
  wsUrl: string;
  onEnvelope: (envelope: WsEnvelope) => void;
  onStateChange?: (state: WidgetSocketState) => void;
  /** Called on every (re)connect to decide whether to send `resume {lastSeq}`. */
  getResumeSeq: () => number | undefined;
  /**
   * Mints a fresh session token. Called only when the server rejects the
   * one in hand (`4401`) — the token is short-lived by design and the API
   * assumes the widget re-mints, so without this a widget left open past
   * the token's hour would retry a dead credential forever.
   */
  refreshSessionToken?: () => Promise<string>;
  heartbeatIntervalMs?: number;
}

const HEARTBEAT_MS = 25_000;

/**
 * How long a connection must stay up before it counts as a real session
 * rather than a failed attempt. Below this, `open` means only that the
 * server accepted the upgrade — it says nothing about whether the session
 * behind it works, and treating it as success is what turns an
 * immediately-dropped connection into an unbounded reconnect flood.
 */
const STABLE_AFTER_MS = 10_000;

/** The API's "missing or invalid token" close code (`routes/widget.ts`). */
const CLOSE_INVALID_TOKEN = 4401;

/** Frames worth holding while the socket is down. A heartbeat isn't: a ping that missed its window tells the server nothing when it finally lands. */
const NEVER_QUEUED = new Set(["ping"]);

/**
 * The shopper's own message — the one frame whose loss is visible to them,
 * as a bubble in the transcript the bot never answers. Held until the
 * server acks it rather than merely until the socket looks writable: a
 * frame written into a connection that dies before the server reads it is
 * gone, and `readyState === OPEN` is true right up until it isn't.
 * Replaying is safe because the server dedupes on `clientMsgId`
 * (`appendMessage` in packages/db) — which is what that field is for.
 */
const ACK_REQUIRED = "msg.send";

/** Bounded so a widget left open on a dead connection can't grow these without limit. */
const MAX_QUEUED = 32;

interface QueuedFrame {
  type: string;
  data: unknown;
}

function clientMsgIdOf(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const id = (data as { clientMsgId?: unknown }).clientMsgId;
  return typeof id === "string" ? id : undefined;
}

/**
 * The one place a socket object exists — no `readyState` checks in view
 * code, no socket reference held anywhere else (PLAN.md "keep the widget's
 * message handling free of WebSocket assumptions"). Reconnects with
 * exponential backoff + jitter capped at 30s, and sends `resume {lastSeq}`
 * on every successful (re)connect so a dropped connection can't lose or
 * duplicate a message — see PLAN.md "Ordering and reconnect" and test 6.
 */
export class WidgetSocket {
  readonly #options: WidgetSocketOptions;
  #socket: WebSocket | undefined;
  #state: WidgetSocketState = "closed";
  #attempt = 0;
  #heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  #reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  #stableTimer: ReturnType<typeof setTimeout> | undefined;
  #closedByCaller = false;
  #wsUrl: string;
  #refreshing = false;
  /** Sends made while the socket was down, replayed in order once it's back. */
  #queue: QueuedFrame[] = [];
  /** `msg.send` frames the server hasn't acked yet, keyed by `clientMsgId`, replayed on every (re)connect. */
  #unacked = new Map<string, unknown>();

  constructor(options: WidgetSocketOptions) {
    this.#options = options;
    this.#wsUrl = options.wsUrl;
  }

  get state(): WidgetSocketState {
    return this.#state;
  }

  connect(): void {
    this.#closedByCaller = false;
    this.#open();
  }

  close(): void {
    this.#closedByCaller = true;
    this.#clearTimers();
    this.#queue = [];
    this.#unacked.clear();
    this.#socket?.close();
  }

  /**
   * Queued rather than dropped when the socket is down. A dropped
   * `msg.send` is invisible to the shopper — their message sits in the
   * transcript under a typing indicator that never resolves, because the
   * server was never told about it.
   */
  send(type: string, data: unknown): void {
    // Recorded before the write, not after: the point is to survive a write
    // that looked like it succeeded.
    if (type === ACK_REQUIRED && !this.#closedByCaller) {
      const id = clientMsgIdOf(data);
      if (id !== undefined) {
        if (this.#unacked.size >= MAX_QUEUED) {
          const oldest = this.#unacked.keys().next().value;
          if (oldest !== undefined) this.#unacked.delete(oldest);
        }
        this.#unacked.set(id, data);
      }
    }

    if (this.#state === "open" && this.#socket) {
      this.#socket.send(JSON.stringify(makeEnvelope(type, data)));
      return;
    }
    if (this.#closedByCaller || NEVER_QUEUED.has(type)) return;
    // A `msg.send` is already held in `#unacked` and replayed from there —
    // queueing it as well would put it on the wire twice.
    if (type === ACK_REQUIRED && clientMsgIdOf(data) !== undefined) return;
    if (this.#queue.length >= MAX_QUEUED) this.#queue.shift();
    this.#queue.push({ type, data });
  }

  #setState(state: WidgetSocketState): void {
    this.#state = state;
    this.#options.onStateChange?.(state);
  }

  #open(): void {
    this.#setState(this.#attempt === 0 ? "connecting" : "reconnecting");
    const socket = new WebSocket(this.#wsUrl);
    this.#socket = socket;

    socket.addEventListener("open", () => {
      if (this.#socket !== socket) return;
      this.#setState("open");
      this.#startHeartbeat();

      // The backoff is *not* reset here. A server that accepts the upgrade
      // and then hangs up — an expired token, a bot whose runtime failed to
      // resolve, a handler that threw — would otherwise pin the retry delay
      // at attempt 0 forever, which is a connection every few hundred
      // milliseconds for as long as the page stays open.
      if (this.#stableTimer) clearTimeout(this.#stableTimer);
      this.#stableTimer = setTimeout(() => {
        if (this.#socket === socket) this.#attempt = 0;
      }, STABLE_AFTER_MS);

      const lastSeq = this.#options.getResumeSeq();
      if (lastSeq !== undefined) this.send("resume", { lastSeq });
      this.#flushQueue();
    });

    socket.addEventListener("message", (event: MessageEvent) => {
      let raw: unknown;
      try {
        raw = JSON.parse(String(event.data));
      } catch {
        return;
      }
      const parsed = wsEnvelopeSchema.safeParse(raw);
      if (!parsed.success) return;
      const envelope = parsed.data as WsEnvelope;
      // The server has it durably (it wrote the row before acking), so it
      // no longer needs replaying.
      if (envelope.type === "msg.ack") {
        const id = clientMsgIdOf(envelope.data);
        if (id !== undefined) this.#unacked.delete(id);
      }
      this.#options.onEnvelope(envelope);
    });

    socket.addEventListener("close", (event: CloseEvent) => {
      if (this.#socket !== socket) return; // a newer socket has already taken over
      this.#clearHeartbeat();
      if (this.#stableTimer) clearTimeout(this.#stableTimer);
      if (this.#closedByCaller) {
        this.#setState("closed");
        return;
      }
      this.#scheduleReconnect(event?.code);
    });

    socket.addEventListener("error", () => {
      socket.close();
    });
  }

  #flushQueue(): void {
    const pending = this.#queue;
    this.#queue = [];
    for (const frame of pending) this.send(frame.type, frame.data);

    // Replayed after the queue so the transcript keeps its original order.
    // Safe to repeat: the server dedupes on `clientMsgId`, so a message it
    // already stored acks again rather than doubling.
    for (const [, data] of this.#unacked) {
      if (this.#state !== "open" || !this.#socket) break;
      this.#socket.send(JSON.stringify(makeEnvelope(ACK_REQUIRED, data)));
    }
  }

  #scheduleReconnect(closeCode?: number): void {
    this.#setState("reconnecting");
    const delay = reconnectDelayMs(this.#attempt);
    this.#attempt += 1;
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);

    // A token the server has already refused can only be refused again, so
    // this one code re-mints before retrying rather than backing off into
    // a longer and longer loop around a dead credential.
    const needsFreshToken = closeCode === CLOSE_INVALID_TOKEN && Boolean(this.#options.refreshSessionToken);

    this.#reconnectTimer = setTimeout(() => {
      if (this.#closedByCaller) return;
      if (needsFreshToken) void this.#reopenWithFreshToken();
      else this.#open();
    }, delay);
  }

  /** Swaps the token the next (re)connect uses — for when the server re-issues one on an open socket. */
  setSessionToken(token: string): void {
    const url = new URL(this.#wsUrl);
    url.searchParams.set("token", token);
    this.#wsUrl = url.toString();
  }

  async #reopenWithFreshToken(): Promise<void> {
    if (this.#refreshing) return;
    this.#refreshing = true;
    try {
      const token = await this.#options.refreshSessionToken?.();
      if (token) this.setSessionToken(token);
      // The backoff is deliberately *not* reset for a fresh token. Minting
      // one proves the mint endpoint works, not that the socket will stay
      // up — and when the two disagree (a session secret that differs
      // between the process that signs and the one that verifies), every
      // reconnect gets a brand-new token and a brand-new 4401. Resetting
      // here would make that its own tight loop.
    } catch {
      // Falls through to a plain retry on the old token — the backoff is
      // already growing, and a mint endpoint that's down will recover.
    } finally {
      this.#refreshing = false;
    }
    if (!this.#closedByCaller) this.#open();
  }

  #startHeartbeat(): void {
    this.#clearHeartbeat();
    this.#heartbeatTimer = setInterval(() => this.send("ping", {}), this.#options.heartbeatIntervalMs ?? HEARTBEAT_MS);
  }

  #clearHeartbeat(): void {
    if (this.#heartbeatTimer) clearInterval(this.#heartbeatTimer);
    this.#heartbeatTimer = undefined;
  }

  #clearTimers(): void {
    this.#clearHeartbeat();
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    if (this.#stableTimer) clearTimeout(this.#stableTimer);
  }
}
